import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

import PageBanner from "../../components/PageBanner";
import RichText from "../../components/content/RichText";
import VocSteps from "../../components/voc/VocSteps";
import VocForm from "../../components/voc/VocForm";
import VocConfirmModal from "../../components/voc/VocConfirmModal";
import VocService, { pickLang } from "../../services/voc";

const EMPTY_FORM = {
  category: "",
  fullName: "",
  email: "",
  contactStatus: "",
  faculty: "",
  educationLevel: "",
  yearOfStudy: "",
  staffType: "",
  detail: "",
  publicConsent: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Voc() {
  const { lang } = useParams();
  const history = useHistory();
  const t = (th, en) => (lang === "th" ? th : en);

  const [setting, setSetting] = useState(null);
  const [step, setStep] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    VocService.getSetting().then(setSetting);
  }, []);

  useEffect(() => {
    if (lang !== "th" && lang !== "en") window.open("/th", "_self");
  }, [lang]);

  /** ตรวจฟอร์มฝั่งเบราว์เซอร์ก่อน จะได้ไม่ต้องรอ server ตอบ */
  const validate = () => {
    const next = {};
    if (!form.category) next.category = t("กรุณาเลือกประเภทเรื่อง", "Please choose a category");
    if (!form.fullName.trim()) next.fullName = t("กรุณากรอกชื่อ", "Please enter your name");
    // อีเมลไม่บังคับ แต่ถ้ากรอกต้องถูกรูปแบบ
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim()))
      next.email = t("กรุณากรอกอีเมลให้ถูกต้อง", "Please enter a valid email");
    if (!form.contactStatus)
      next.contactStatus = t("กรุณาเลือกสถานภาพ", "Please choose your status");
    if (!form.detail.trim()) next.detail = t("กรุณากรอกรายละเอียด", "Please enter details");

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReview = () => {
    if (validate()) {
      setSubmitError("");
      setConfirming(true);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const res = await VocService.submit(form, files);
    setSubmitting(false);

    if (res.ok) {
      setConfirming(false);
      setStep(3);
      window.scrollTo(0, 0);
      return;
    }

    // server ปฏิเสธ — เอา error รายช่องมาแสดงแล้วกลับไปหน้าฟอร์ม
    setErrors(res.errors || {});
    setSubmitError(res.message);
    setConfirming(false);
  };

  const bannerText = [{ name: "Voice of Customer" }];

  // ข้อความจาก VOC Setting มีทั้งฉบับไทยและอังกฤษ ช่องอังกฤษเว้นว่างได้
  const introTitle = pickLang(setting, "introTitle", lang);
  const introText = pickLang(setting, "introText", lang);
  const terms = pickLang(setting, "terms", lang);
  const successMessage = pickLang(setting, "successMessage", lang);

  return (
    <div id="voc" className="pt-[135px] lg:pt-[0px]">
      <PageBanner
        banner={setting?.banner}
        text_th="Voice of Customer"
        text_en="Voice of Customer"
        bannerText={bannerText}
        lang={lang}
      />

      <div className="section">
        <div className="page-container">
          <div className="mt-[32px] sm:mt-[48px] mb-[64px] sm:mb-[96px]">
            <h1 className="text-body sm:text-heading font-bold text-slateGray text-left">
              {introTitle ||
                t(
                  "ช่องทางสื่อสารกับกลุ่มงานบริการสุขภาพและอนามัย",
                  "Talk to the Health Care Unit"
                )}
            </h1>
            {introText && (
              <p className="text-meta sm:text-body text-slateGray text-left mt-[6px] sm:mt-[10px] whitespace-pre-line">
                {introText}
              </p>
            )}

            <VocSteps
              current={step}
              lang={lang}
              onSelect={(no) => setStep(no)}
            />

            {/* ขั้นตอนที่ 1 — ข้อตกลงการใช้ระบบ */}
            {step === 1 && (
              <div className="mt-[32px]">
                <h2 className="text-title sm:text-subheading font-bold text-slateGray text-left after:block after:w-[64px] after:h-[3px] after:bg-brand after:mt-[6px]">
                  {t("ข้อตกลงการใช้ระบบ", "Terms of Use")}
                </h2>

                <div className="mt-[20px]">
                  {terms ? (
                    <RichText html={terms} />
                  ) : (
                    <p className="text-body text-metaGray text-left">
                      {t(
                        "ยังไม่ได้กำหนดข้อตกลงการใช้ระบบ กรุณาเพิ่มที่หน้าจัดการ VOC Setting",
                        "Terms have not been set yet."
                      )}
                    </p>
                  )}
                </div>

                <label className="flex items-start gap-[10px] cursor-pointer mt-[28px] text-left">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-[4px] w-[18px] h-[18px] accent-[#1CB5B1] shrink-0"
                  />
                  <span className="text-meta text-slateGray">
                    {t(
                      "ข้าพเจ้าได้อ่านข้อตกลงเป็นอย่างดีแล้ว และยอมรับตามข้อตกลงนี้",
                      "I have read and accept these terms"
                    )}
                    <span className="text-brand">*</span>
                  </span>
                </label>

                <button
                  type="button"
                  disabled={!accepted}
                  onClick={() => {
                    setStep(2);
                    window.scrollTo(0, 0);
                  }}
                  className="w-full mt-[20px] rounded-[8px] bg-brand text-white text-body font-bold py-[14px] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("ยอมรับและเริ่มใช้งาน", "Accept and continue")}
                </button>
              </div>
            )}

            {/* ขั้นตอนที่ 2 — กรอกข้อมูล */}
            {step === 2 && (
              <>
                {submitError && (
                  <div className="mt-[24px] rounded-[8px] border-[1px] border-brand bg-brand/5 px-[16px] py-[12px]">
                    <p className="text-meta text-brand text-left">{submitError}</p>
                  </div>
                )}
                <VocForm
                  value={form}
                  onChange={setForm}
                  files={files}
                  onFilesChange={setFiles}
                  errors={errors}
                  lang={lang}
                  setting={setting}
                  onSubmit={handleReview}
                />
              </>
            )}

            {/* ขั้นตอนที่ 3 — บันทึกสำเร็จ */}
            {step === 3 && (
              <div className="mt-[32px] sm:mt-[48px] text-center">
                <h2 className="flex items-center justify-center gap-[12px] text-title sm:text-subheading font-bold text-slateGray">
                  {t("บันทึกข้อมูลสำเร็จ", "Submitted successfully")}
                  <FontAwesomeIcon icon={faCircleCheck} className="text-blue" />
                </h2>

                <p className="text-meta sm:text-body text-slateGray mt-[10px] max-w-[720px] mx-auto whitespace-pre-line">
                  {successMessage ||
                    t(
                      "ขอบคุณสำหรับข้อมูลที่ท่านแจ้งเข้ามา เจ้าหน้าที่จะตรวจสอบและติดต่อกลับทางอีเมลที่ท่านให้ไว้",
                      "Thank you. Our team will review your message and reply by email."
                    )}
                </p>

                <div className="flex justify-center mt-[28px]">
                  <button
                    type="button"
                    onClick={() => history.push(`/${lang}`)}
                    className="rounded-[6px] bg-brand text-white text-body font-bold px-[40px] py-[12px] hover:opacity-90"
                  >
                    {t("กลับสู่หน้าหลัก", "Back to home")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <VocConfirmModal
        open={confirming}
        value={form}
        files={files}
        lang={lang}
        submitting={submitting}
        onCancel={() => setConfirming(false)}
        onConfirm={handleSubmit}
      />
    </div>
  );
}
